/**
 * OnboardingResumeBanner v1.1 (2026-10-08, cc_tasks row 520, T560)
 *   The amber "Finish setup / Not now" banner is now ONE QUIET LINE, as approved on
 *   https://mentionfox.com/preview/first-screen-31l8p9m0abcv41vg ("Proposed"):
 *   "Your free trial runs 5 days and includes the Pro plan's features, watching up to 25
 *   companies with instant alerts, and 10 credits." then "What Pro includes" (the plans page,
 *   new tab) and "Set up your account (optional)", which opens account setup right here, under
 *   the line, without leaving the page. Every figure comes from src/lib/trialFacts.ts (the
 *   account's trial_ends_at and credit grant, the plan's watch limit, tier-limits.ts); none is
 *   typed. A paid account sees no line; AppLayout mounts this only while setup is unfinished.
 *   A free account (no trial running) sees only the optional setup control.
 * v1.0 (2026-09-30, cc_tasks row 310, T344): the destination comes first; setup is offered here
 *   and never forced (src/lib/post-auth-route.ts onboardingDeferred).
 */
import { lazy, Suspense, useEffect, useId, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Tip } from "@/components/Tip";
import { useTrialFacts, isPaidTier, trialSentence } from "@/lib/trialFacts";

const Onboarding = lazy(() => import("@/pages/Onboarding"));

function setupAskedInAddress(): boolean {
  try { return new URLSearchParams(window.location.search).get("setup") === "open"; } catch { return false; }
}

export function OnboardingResumeBanner() {
  const facts = useTrialFacts();
  const [open, setOpen] = useState(false);
  const regionId = useId();

  // A link from a public page ("Set up your account (optional)" next to a watched company)
  // arrives with ?setup=open: the setup opens under the line straight away.
  useEffect(() => { if (setupAskedInAddress()) setOpen(true); }, []);

  if (facts === undefined) return null; // the plan is still loading: no flash of the wrong line
  if (facts && isPaidTier(facts.tier)) return null;

  const sentence = trialSentence(facts);

  return (
    <div className="px-4 pt-2 pb-1 text-[13px] leading-snug text-muted-foreground" data-foxchat-id="first-screen-quiet-line">
      <p role="status" className="m-0">
        {sentence ? <span>{sentence} </span> : null}
        {sentence ? (
          <>
            <Tip text="Opens the plans page in a new tab, with everything the Pro plan includes. This page stays where it is.">
              <a
                href="/pricing"
                target="_blank"
                rel="noopener"
                className="underline underline-offset-2 hover:text-foreground"
                data-testid="quiet-line-pro"
              >
                What Pro includes
              </a>
            </Tip>
            <span aria-hidden="true" className="mx-2">·</span>
          </>
        ) : null}
        <Tip text={open
          ? "Closes account setup. Steps you finished are kept, and you can open it again here."
          : "Optional. Opens account setup right here, under this line, so you stay on this page. It tailors MentionFox to your work."}>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls={regionId}
            className="inline-flex items-center gap-1 hover:text-foreground"
            data-testid="quiet-line-setup"
          >
            Set up your account (optional)
            {open ? <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />}
          </button>
        </Tip>
      </p>
      {open ? (
        <div
          id={regionId}
          role="region"
          aria-label="Account setup"
          className="mt-2 max-h-[80vh] overflow-y-auto rounded-lg border border-border"
          // transform keeps the setup's fixed-position pieces inside this box instead of the window.
          style={{ transform: "translateZ(0)" }}
          data-testid="quiet-line-setup-region"
        >
          <Suspense fallback={<div className="p-3 text-xs">Opening account setup…</div>}>
            <Onboarding />
          </Suspense>
        </div>
      ) : null}
    </div>
  );
}
